
import { useState } from 'react';
import { toast } from 'sonner';
import JSZip from 'jszip';
import { Menu, Music, Mic } from 'lucide-react';
import AudioUploader from '@/components/AudioUploader';
import { SessionHeader } from '@/components/session/SessionHeader';
import { ProcessingStatus } from '@/components/session/ProcessingStatus';
import { StemsList } from '@/components/session/StemsList';
import WaveformDisplay from '@/components/WaveformDisplay';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import type { Stem } from '@/types/stem';

interface SessionContentProps {
  session: any;
  isProcessing: boolean;
  progress: number;
  processingError: string | null;
  processingDuration: number;
  onRetry: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
  onFileSelect: (file: File, type: 'stem' | 'acapella' | 'drum') => void;
  sessionType: 'stem' | 'acapella' | 'drum';
}

export const SessionContent = ({
  session,
  isProcessing,
  progress,
  processingError,
  processingDuration,
  onRetry,
  onDelete,
  onFileSelect,
  sessionType,
}: SessionContentProps) => {
  const isMobile = useIsMobile();
  const { toggleSidebar } = useSidebar();

  const handleDownload = async (stem: Stem) => {
    try {
      const response = await fetch(stem.url);
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${stem.name.toLowerCase()}.mp3`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Downloaded ${stem.name} successfully`);
    } catch (error) {
      console.error('Error downloading stem:', error);
      toast.error(`Failed to download ${stem.name}`);
    }
  };

  const handleDownloadAll = async () => {
    if (!session?.stems) return;

    try {
      toast.info('Preparing stems for download...');
      
      const downloadPromises = (session.stems as Stem[]).map(async (stem) => {
        const response = await fetch(stem.url);
        const blob = await response.blob();
        return {
          name: stem.name.toLowerCase(),
          blob
        };
      });

      const downloads = await Promise.all(downloadPromises);
      const zip = new JSZip();
      downloads.forEach(({ name, blob }) => {
        zip.file(`${name}.mp3`, blob);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${session.original_filename.replace(/\.[^/.]+$/, '')}_stems.zip`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('All stems downloaded successfully');
    } catch (error) {
      console.error('Error downloading all stems:', error);
      toast.error('Failed to download stems');
    }
  };

  if (!session) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              onClick={toggleSidebar}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              {sessionType === 'stem' ? (
                <Music className="h-6 w-6 text-white" />
              ) : (
                <Mic className="h-6 w-6 text-white" />
              )}
              <h1 className="text-2xl font-semibold tracking-tight text-white">
                {sessionType === 'stem' ? 'AI Stem Splitter' : 'Acapella Extractor'}
              </h1>
            </div>
            <p className="text-sm text-gray-400">
              {sessionType === 'stem' 
                ? 'Upload audio to split stems'
                : 'Upload audio to extract acapellas'
              }
            </p>
          </div>
        </div>
        <AudioUploader onFileSelect={(file) => onFileSelect(file, sessionType)} />
        {isProcessing && (
          <ProcessingStatus
            isProcessing={isProcessing}
            progress={progress}
            error={null}
            onRetry={() => {}}
            processingDuration={processingDuration}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-8">
      <SessionHeader
        filename={session.original_filename}
        sessionId={session.id}
        onDelete={() => onDelete(session.id)}
        onDownloadAll={handleDownloadAll}
        showDownload={session.status === 'completed' && session.stems && (session.stems as Stem[]).length > 0}
      />

      {session.status === 'processing' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-card rounded-lg">
            <WaveformDisplay
              audioUrl={session.original_audio_url}
              name="Original"
              index={0}
            />
          </div>
          <ProcessingStatus
            isProcessing={true}
            progress={progress}
            error={null}
            onRetry={() => {}}
            processingDuration={processingDuration}
          />
        </div>
      )}

      {session.status === 'failed' && (
        <ProcessingStatus
          isProcessing={isProcessing}
          progress={progress}
          error="There was an error processing your audio file."
          onRetry={() => onRetry(session.id)}
          processingDuration={processingDuration}
        />
      )}

      {processingError && (
        <ProcessingStatus
          isProcessing={isProcessing}
          progress={progress}
          error={processingError}
          onRetry={() => onRetry(session.id)}
          processingDuration={processingDuration}
        />
      )}

      {session.stems && session.status === 'completed' && (session.stems as Stem[]).length > 0 && (
        <StemsList
          stems={session.stems as Stem[]}
          originalUrl={session.original_audio_url}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
};
